<template>
  <!-- Shared photo grid (S&R gallery + Universal Quote before/after images).
       Figma SAR "Service & Repair - Details - Step 3" (5506:2063) / UQ "Equipment Info - Step 4":
       4 thumbnails a page on laptop (210x120, 5px #EFEFEF border), hover delete (red) + view (purple), page dots;
       "Step 4/5 Photo Modal": photo, red X, prev / next at the bottom. -->
  <div class="font-roboto">
    <!-- carousel: all photos in one row, prev / next moves one photo (1 2 3 → 2 3 4) -->
    <div class="overflow-hidden">
    <div class="flex gap-[20px] transition-transform duration-300 ease-in-out"
      :style="{ transform: `translateX(calc(${-start} * (100% + 20px) / ${perPage}))` }">
      <div v-for="(file, i) in files" :key="file.id || file.url"
        class="group relative flex-shrink-0 aspect-[210/120] rounded-[10px] border-[5px] border-[#EFEFEF] overflow-hidden bg-[#EFEFEF]"
        :style="{ width: `calc((100% - ${(perPage - 1) * 20}px) / ${perPage})` }" :title="file.title">
        <div v-if="file.mime === 'pdf'" class="w-full h-full flex flex-col items-center justify-center gap-[6px] bg-white">
          <img src="../../assets/images/pdf-icon.svg" alt="" class="w-[36px] h-[36px]">
          <span class="px-[8px] max-w-full truncate text-[12px] text-[#5B616B]">{{ file.title }}</span>
        </div>
        <img v-else :src="file.url" :alt="file.title || altText" class="w-full h-full object-cover select-none">
        <!-- still uploading -->
        <div v-if="file.isLoading" class="absolute inset-0 flex items-center justify-center bg-[rgba(255,255,255,0.6)]">
          <span class="spinner-border spinner-border-sm text-[#A084E8]" role="status" aria-label="Uploading" />
        </div>
        <!-- hover: delete (red) + view (purple), as in Figma; touch screens have no hover, so there they always show,
             small and stacked in the top-right corner (Figma mobile 5908-2374).
             opacity-[0], not opacity-0: Bootstrap's .opacity-0 is !important and would block the hover -->
        <div v-else
          class="absolute inset-0 flex items-center justify-center gap-[10px] bg-[rgba(0,0,0,0.35)] opacity-[0] transition-opacity group-hover:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100 [@media(hover:none)]:inset-auto [@media(hover:none)]:top-[6px] [@media(hover:none)]:right-[6px] [@media(hover:none)]:flex-col-reverse [@media(hover:none)]:gap-[6px] [@media(hover:none)]:bg-transparent">
          <button v-if="deletable" type="button" aria-label="Delete photo"
            class="w-[30px] h-[30px] [@media(hover:none)]:w-[24px] [@media(hover:none)]:h-[24px] rounded-full flex items-center justify-center border-0 bg-[#F67280]"
            @click.stop="$emit('remove', file)">
            <!-- Figma (5795:3814): trash with lid and two lines -->
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 6.5h16M9.5 6.5V4h5v2.5M6.5 6.5l.9 13.5h9.2l.9-13.5M10.3 10v6.5M13.7 10v6.5" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>
          </button>
          <button type="button" aria-label="View photo"
            class="w-[30px] h-[30px] [@media(hover:none)]:w-[24px] [@media(hover:none)]:h-[24px] rounded-full flex items-center justify-center border-0 bg-[#A084E8]"
            @click.stop="openViewer(i)">
            <!-- Figma (5795:3814): zoom-in magnifier -->
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="10.5" cy="10.5" r="7" stroke="#FFFFFF" stroke-width="2.4" /><path d="M15.8 15.8 21 21M10.5 7.5v6M7.5 10.5h6" stroke="#FFFFFF" stroke-width="2.4" stroke-linecap="round" /></svg>
          </button>
        </div>
      </div>
    </div>
    </div>
    <!-- position dots (Figma 5795:3814): grey pill, 12px dots, active purple; laptop 4 a page, tablet 3, mobile 2 -->
    <div v-if="maxStart > 0" class="flex items-center justify-center gap-[12px] mt-[20px]">
      <button type="button" aria-label="Previous photo" :disabled="start === 0"
        class="w-[26px] h-[26px] rounded-full flex items-center justify-center border-0"
        :class="start === 0 ? 'bg-transparent' : 'bg-[#F67280]'" @click="start--">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M15 5l-7 7 7 7" :stroke="start === 0 ? '#999999' : '#FFFFFF'" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" /></svg>
      </button>
      <!-- all dots on one track, a 3-dot window slides over it (smooth, like an endless carousel);
           the window's edge dots shrink when more positions lie beyond them -->
      <div class="px-[8px] py-[4px] rounded-full bg-[#EFEFEF]">
        <div class="overflow-hidden" :style="{ width: dotWindow * DOT_STEP - DOT_GAP + 'px' }">
          <div class="flex items-center gap-[8px] transition-transform duration-300 ease-out"
            :style="{ transform: `translateX(-${dotFrom * DOT_STEP}px)` }">
            <button v-for="n in maxStart + 1" :key="n" type="button" :aria-label="'Photos from ' + n"
              class="flex-shrink-0 w-[12px] h-[12px] p-0 rounded-full border-1 transition-all duration-300"
              :class="[start === n - 1 ? 'bg-[#A084E8] border-[#A084E8]' : 'bg-white border-[#CCCCCC]', dotShrunk(n - 1) && 'scale-[0.6]']"
              :tabindex="n - 1 < dotFrom || n - 1 >= dotFrom + dotWindow ? -1 : 0" @click="start = n - 1" />
          </div>
        </div>
      </div>
      <button type="button" aria-label="Next photo" :disabled="start >= maxStart"
        class="w-[26px] h-[26px] rounded-full flex items-center justify-center border-0"
        :class="start >= maxStart ? 'bg-transparent' : 'bg-[#F67280]'" @click="start++">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 5l7 7-7 7" :stroke="start >= maxStart ? '#999999' : '#FFFFFF'" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" /></svg>
      </button>
    </div>

    <!-- photo modal -->
    <PhotoViewer v-model:index="viewerIndex" :files="files" :alt-text="altText" />
  </div>
</template>

<script>
import PhotoViewer from "./PhotoViewer.vue";

export default {
  name: "PhotoGallery",
  components: { PhotoViewer },
  props: {
    // [{ id?, url, title?, mime?: 'pdf' | 'image', isLoading? }]
    files: { type: Array, default: () => [] },
    deletable: { type: Boolean, default: true },
    altText: { type: String, default: "Photo" },
  },
  emits: ["remove"],
  created() {
    this.DOT_STEP = 20; // 12px dot + 8px gap
    this.DOT_GAP = 8;
  },
  data() {
    return {
      start: 0, // index of the first visible photo
      perPage: 4,
      viewerIndex: null,
    };
  },
  computed: {
    // last start that still fills the row
    // at most 3 dots visible: the window slides so the current one sits in the middle (ends excepted)
    dotWindow() {
      return Math.min(this.maxStart + 1, 3);
    },
    dotFrom() {
      return Math.min(Math.max(this.start - 1, 0), this.maxStart + 1 - this.dotWindow);
    },
    maxStart() {
      return Math.max(0, (this.files?.length || 0) - this.perPage);
    },
  },
  watch: {
    // a delete / resize can leave the row past the end
    maxStart() {
      if (this.start > this.maxStart) this.start = this.maxStart;
    },
  },
  mounted() {
    this.checkPerPage();
    window.addEventListener("resize", this.checkPerPage);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.checkPerPage);
  },
  methods: {
    // an edge dot of the window with more positions beyond it (not the active one)
    dotShrunk(i) {
      if (i === this.start) return false;
      const last = this.dotFrom + this.dotWindow - 1;
      return (i === this.dotFrom && i > 0) || (i === last && last < this.maxStart);
    },
    // laptop 4 a page (Figma), tablet 3, mobile 2 — matches the grid columns
    checkPerPage() {
      const w = window.innerWidth;
      this.perPage = w >= 1024 ? 4 : w >= 640 ? 3 : 2;
    },
    openViewer(index) {
      this.viewerIndex = index;
    },
  },
};
</script>
